import { Injectable, inject } from '@angular/core';
import { BoelsCatalogsResponse } from '@boels/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { SET_ACTIVE_CULTURE } from '../../cultures/actions/cultures.actions';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { BoelsState } from '../../reducers.index';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import {
  GET_CATALOGS_FOR_ACTIVE_BASESITE_AND_CULTURE,
  LOAD_CATALOGS,
  loadCatalogs,
  loadCatalogsFail,
  loadCatalogsSuccess,
} from '../actions/catalogs.action';
import { CatalogsGateway } from '../gateway/catalogs.gateway';
import { selectCatalogsEntities } from '../selectors/catalog.selector';

@Injectable({
  providedIn: null,
})
export class CatalogsEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<BoelsState>>(Store);
  private catalogsGateway = inject(CatalogsGateway);
  private siteContextFacade = inject(SiteContextFacade);
  private cultureFacade = inject(CultureFacade);

  getCatalogsForActiveBaseSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_CATALOGS_FOR_ACTIVE_BASESITE_AND_CULTURE),
      concatLatestFrom(() => [
        this.store.select(selectCatalogsEntities),
        this.siteContextFacade.getActiveBaseSiteEntity(),
        this.cultureFacade.getActiveCulture(),
      ]),
      filter(([, catalogDictionary, activeBaseSite, activeCulture]) => {
        if (activeBaseSite?.uid && activeCulture) {
          return !catalogDictionary?.[activeCulture];
        }
        return false;
      }),
      map(([, , activeBaseSite, activeCulture]) =>
        loadCatalogs({ baseSite: activeBaseSite.uid, culture: activeCulture }),
      ),
    ),
  );

  getCatalogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_ACTIVE_CULTURE),
      withLatestFrom(
        this.store.select(selectCatalogsEntities),
        this.siteContextFacade.getActiveBaseSiteEntity(),
      ),
      filter(
        ([props, catalogDictionary]) =>
          !catalogDictionary?.[
            props['payload'] && props['payload'] !== 'gneric'
          ],
      ),
      map(([props, , baseSite]) => {
        return loadCatalogs({
          baseSite: baseSite.uid,
          culture: props['payload'],
        });
      }),
    ),
  );

  loadCatalogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_CATALOGS),
      exhaustMap((action) => {
        return this.catalogsGateway.getCatalogs(action['baseSite']).pipe(
          map((catalogsResponse: BoelsCatalogsResponse) =>
            loadCatalogsSuccess({
              catalogs: catalogsResponse,
              culture: action['culture'],
            }),
          ),
          catchError((errorMessage) => of(loadCatalogsFail({ errorMessage }))),
          take(1),
        );
      }),
    ),
  );
}
